import { t } from "@lingui/core/macro";

import { Button } from "_/components/button";
import { useModal } from "_/components/modal";
import { Redirect, useRoute } from "_/components/router";
import { InviteUserForm } from "_/components/settings-view";
import { PermissionTest } from "_/components/permission-test";

import { AdminMachinesSection } from "_/components/admin-view/machines";

import { Org, useCurrentOrg, useOrg, useOrgs } from "_/data/orgs";
import { useOrgInvitesCount } from "_/data/invitations";
import { permissions } from "_/data/rbac";

import { adminRoutes, adminUrls } from "_/routes";

import { InvitationsList } from "./invitationsTable";
import { UsersList } from "./usersTable";
import { AdminPage } from "../../admin-page";

import * as S from "./styled";

// Component to display when the user does not have permission to view the org.
function InsufficientOrgPerms() {
  return (
    <S.OrgDeniedContainer>
      <S.OrgDeniedMessage>
        {t`components.admin-view.insufficient-org-perms`}
      </S.OrgDeniedMessage>
    </S.OrgDeniedContainer>
  );
}

export const OrgOverview = () => {
  const [_, orgOverviewParams] = useRoute(adminRoutes.organization);
  const orgId = orgOverviewParams?.id;
  const inviteModal = useModal<Org>();
  const { data: org } = useOrg(orgId);
  const { data: orgs } = useOrgs();
  const { data: currentOrg } = useCurrentOrg();
  const { data: invitationCount = 0 } = useOrgInvitesCount(orgId);

  if (!orgs || !org || !currentOrg) return <></>;

  // Filter out organizations that the user does not have permission to view
  const readOrgs = orgs.filter((organization) =>
    organization.permissions.includes(permissions.organizations.read)
  );

  // Check if the user has permission to view the org based on whether the org
  // is in the list of orgs the user can read.
  const canReadOrg = readOrgs.some((o) => o.id === orgId);

  // Check if the user is trying to view the current org.
  const viewingCurrentOrg = currentOrg?.id === orgId;

  if (!org || !canReadOrg) {
    // If the user does not have permission to view the the current org, display
    // an error message rather than redirecting to the main admin page; redirecting
    // in this case could cause an infinite loop. This case should never happen, as
    // the user should have read permissions to an org they are a member of.
    return viewingCurrentOrg ? (
      <InsufficientOrgPerms />
    ) : (
      <Redirect to={adminUrls.index} />
    );
  }

  const inviteButton = (
    <PermissionTest
      domainId={org.domainId}
      requiredPermissions={"users/invite"}
      render={({ allowed }) => (
        <Button disabled={!allowed} onClick={() => inviteModal.openModal(org)}>
          {t`common.invite-user`}
        </Button>
      )}
    />
  );

  const orgName = org.name;

  return (
    <AdminPage title={t`components.admin-view.admin-title ${orgName}`}>
      <AdminMachinesSection variant="orgDetail" orgDetail={org} />

      {invitationCount > 0 && (
        <S.AdminContentSection>
          <S.AdminContentSectionTitleContainer>
            <h2>{t`common.invitations`}</h2>
            {inviteButton}
          </S.AdminContentSectionTitleContainer>
          <InvitationsList org={org} />
        </S.AdminContentSection>
      )}

      <S.AdminContentSection>
        <S.AdminContentSectionTitleContainer>
          <h2>{t`common.users`}</h2>
          {invitationCount > 0 ? null : inviteButton}
        </S.AdminContentSectionTitleContainer>
        <UsersList org={org} />
      </S.AdminContentSection>

      {inviteModal.isModalOpen && (
        <inviteModal.Modal title={t`common.invite-user-to-org ${org.name}`}>
          <InviteUserForm org={org} onClose={inviteModal.closeModal} />
        </inviteModal.Modal>
      )}
    </AdminPage>
  );
};
